import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const [activeLink, setActiveLink] = useState('home');
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      setScrolled(currentScroll > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update active link based on current route
  useEffect(() => {
    const path = location.pathname;
    let id = 'home';
    if (path.startsWith('/about')) id = 'aboutus';
    else if (path.startsWith('/shop')) id = 'shop';
    else if (path.startsWith('/blog')) id = 'blogs';
    else if (path.startsWith('/contact')) id = 'contact';
    setActiveLink(id); 
  }, [location.pathname]);

  const handleNavClick = (link) => {
    setActiveLink(link);
    if (window.innerWidth < 1024) {
      setIsExpanded(false);
    }
  };
  const { count } = useCart();
  const { t } = useTranslation('common');
  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 bg-cream py-6 transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-[#303030] tracking-wide" aria-label="Home">
            <img src="/assets/imgs/Home/lalalogo.png" alt="Company logo" className="h-16 w-30" />
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-0.5 bg-gold mb-1.5"></div>
            <div className="w-6 h-0.5 bg-gold mb-1.5"></div>
            <div className="w-6 h-0.5 bg-gold"></div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-8 xl:gap-12">
            {[
              { id: 'home', label: t('nav.home'), to: '/' },
              { id: 'aboutus', label: t('nav.about'), to: '/about' },
              { id: 'shop', label: t('nav.shop'), to: '/shop' },
              { id: 'blogs', label: t('nav.blogs'), to: '/blog' },
              { id: 'contact', label: t('nav.contact'), to: '/contact' }
            ].map((item) => {
              const id = item.id;
              const isHash = item.to.startsWith('/#');
              const linkClasses = `text-sm font-medium tracking-wide transition-all duration-300 relative ${
                activeLink === id
                  ? "text-text-dark font-bold after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-3/5 after:h-0.5 after:bg-gold"
                  : 'text-text-gray hover:text-text-dark'
              }`;
              return isHash ? (
                <a key={id} href={item.to.replace('/', '')} className={linkClasses}>
                  {item.label}
                </a>
              ) : (
                <Link key={id} to={item.to} className={linkClasses} onClick={() => handleNavClick(id)}>
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Right Icons */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Search Icon */}
            <button className="p-2 hover:scale-110 transition-transform">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>

            {/* Cart Icon */}
            <Link to="/cart" className="relative">
              <div className="w-10 h-10 bg-[#6d534e] rounded-full flex items-center justify-center hover:bg-[#5a433f] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d5cdcc" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">{count}</span>
                )}
              </div>
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {isExpanded && (
          <div className="lg:hidden pt-4 pb-4 border-t border-cream-dark">
            {/* Navigation Links and Icons in same row */}
            <div className="flex justify-between items-start">
              {/* Navigation Links - Left aligned */}
              <div className="flex flex-col gap-3">
                {[
                  { id: 'home', label: t('nav.home'), to: '/' },
                  { id: 'aboutus', label: t('nav.about'), to: '/about' },
                  { id: 'shop', label: t('nav.shop'), to: '/shop' },
                  { id: 'blogs', label: t('nav.blogs'), to: '/blog' },
                  { id: 'contact', label: t('nav.contact'), to: '/contact' }
                ].map((item) => {
                  const id = item.id;
                  const isHash = item.to.startsWith('/#');
                  const linkClasses = `text-sm font-medium tracking-wide ${
                    activeLink === id ? 'text-text-dark font-bold' : 'text-text-gray'
                  }`;
                  return isHash ? (
                    <a key={id} href={item.to.replace('/', '')} className={linkClasses}>
                      {item.label}
                    </a>
                  ) : (
                    <Link key={id} to={item.to} className={linkClasses} onClick={() => handleNavClick(id)}>
                      {item.label}
                    </Link>
                  );
                })}
              </div>

              {/* Mobile Icons - Right aligned at same level as first nav item */}
              <div className="flex items-center gap-4 mt-0">
                {/* Search Icon */}
                <button className="p-2 hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                </button>

                {/* Cart Icon */}
                <Link to="/cart" className="relative">
                  <div className="w-10 h-10 bg-[#6d534e] rounded-full flex items-center justify-center hover:bg-[#5a433f] transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d5cdcc" strokeWidth="2">
                      <circle cx="9" cy="21" r="1"></circle>
                      <circle cx="20" cy="21" r="1"></circle>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                    {count > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">{count}</span>
                    )}
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
